import {
  GAME_CONFIG_STORAGE_KEY,
  defaultGameConfig,
  gameConfigDocument,
  sanitizeGameConfig,
  type GameConfigDocument,
  type PublicMatchMode,
} from './contract';
import type { GameConfigClient } from './GameConfigClient';

function storageAvailable(): boolean {
  try {
    const key = '__skr_game_config_probe__';
    localStorage.setItem(key, '1');
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/** Browser stand-in for the public match document. One config per origin. */
export class LocalGameConfig implements GameConfigClient {
  readonly kind: 'local' | 'memory';
  private memory: GameConfigDocument = defaultGameConfig();

  constructor() {
    this.kind = storageAvailable() ? 'local' : 'memory';
    if (this.kind === 'local') this.memory = this.read();
  }

  async load(): Promise<GameConfigDocument> {
    if (this.kind === 'local') this.memory = this.read();
    return this.memory;
  }

  async setPublicMatchMode(mode: PublicMatchMode, adminId: string): Promise<GameConfigDocument> {
    const next = gameConfigDocument(mode, adminId || null);
    this.memory = next;
    if (this.kind === 'local') {
      try {
        localStorage.setItem(GAME_CONFIG_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* keep the in-memory copy */
      }
    }
    return next;
  }

  private read(): GameConfigDocument {
    try {
      const raw = localStorage.getItem(GAME_CONFIG_STORAGE_KEY);
      if (!raw) return defaultGameConfig();
      return sanitizeGameConfig(JSON.parse(raw) as unknown);
    } catch {
      return defaultGameConfig();
    }
  }
}
